import { LayoutDashboard, Share2, Star, StarOff, Trash2 } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { SavedDashboard } from "../services/dashboard-service"

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

type DashboardListItemProps = {
  dashboard: SavedDashboard
  variant: "expanded" | "collapsed"
  onClick: () => void
  onToggleFavorite: (e: React.MouseEvent, id: string) => void
  onDelete: (dashboard: SavedDashboard) => void
}

export function DashboardListItem({
  dashboard,
  variant,
  onClick,
  onToggleFavorite,
  onDelete,
}: DashboardListItemProps) {
  const { t } = useTranslation()
  const groupName = variant === "collapsed" ? "collapsed-item" : "sidebar-item"

  const isSharedWithMe =
    dashboard.currentUserRole !== undefined && dashboard.currentUserRole !== "owner"
  const hasUnviewedInvite = !!dashboard.hasUnviewedNotification
  const inviteTooltip = dashboard.invitationNotification
    ? t("dash.sharing.invited_by", {
        defaultValue: "{{name}} invited you as {{role}}",
        name: dashboard.invitationNotification.invitedByDisplayName,
        role: dashboard.invitationNotification.role,
      })
    : t("dash.sharing.shared_with_you", { defaultValue: "Shared with you" })

  return (
    <div key={dashboard.id} className={`group/${groupName} relative`}>
      <button
        className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors"
        type="button"
        onClick={onClick}
      >
        <LayoutDashboard className="text-muted-foreground size-3.5 shrink-0" />
        <span className="truncate">{dashboard.title || t("dash.dashboard.untitled")}</span>
        {/* Shared-with-me indicator. A pulsing dot announces an
            unseen invitation; once the user opens the dashboard the
            mark-viewed call clears it and we fall back to a steady
            Share icon so the user can still tell at a glance that
            this dashboard isn't theirs. Owned dashboards render
            nothing here. */}
        {isSharedWithMe && variant === "expanded" && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span aria-label={inviteTooltip} className="ml-1 inline-flex shrink-0 items-center">
                  {hasUnviewedInvite ? (
                    <span className="bg-primary relative size-2 rounded-full">
                      <span className="bg-primary absolute inset-0 animate-ping rounded-full opacity-70" />
                    </span>
                  ) : (
                    <Share2 className="text-muted-foreground size-3" />
                  )}
                </span>
              </TooltipTrigger>
              <TooltipContent className="text-xs" side="right">
                {inviteTooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {/* Quality-score chip — colored by score band. Hidden in the
            collapsed sidebar variant since the row is tiny there.
            Score 0/null = never reviewed; we don't render anything. */}
        {variant === "expanded" &&
          dashboard.quality_review &&
          dashboard.quality_review.score > 0 && (
            <span
              aria-label={`AI quality score: ${dashboard.quality_review.score}/10`}
              className={
                "ml-auto inline-flex h-4 shrink-0 items-center rounded px-1 font-mono text-[10px] tabular-nums " +
                (dashboard.quality_review.score >= 8
                  ? "bg-success/15 border-success text-foreground border"
                  : dashboard.quality_review.score >= 5
                    ? "bg-warning/30 text-warning-ink"
                    : "bg-destructive/15 border-destructive text-foreground border")
              }
              title={dashboard.quality_review.summary}
            >
              {dashboard.quality_review.score}
            </span>
          )}
      </button>
      <div
        className={
          variant === "collapsed"
            ? "absolute top-1/2 right-1 flex -translate-y-1/2 items-center gap-0.5"
            : "absolute top-1/2 right-1 flex -translate-y-1/2 items-center gap-0.5 opacity-0 transition group-focus-within/sidebar-item:opacity-100 group-hover/sidebar-item:opacity-100"
        }
      >
        <button
          type="button"
          aria-label={
            dashboard.is_favorite
              ? t("dash.landing.remove_favorite")
              : t("dash.landing.mark_as_favorite")
          }
          className={
            variant === "collapsed"
              ? `text-muted-foreground rounded p-1 opacity-0 transition group-focus-within/${groupName}:opacity-100 group-hover/${groupName}:opacity-100 hover:text-warning`
              : "text-muted-foreground hover:text-warning rounded p-1"
          }
          onClick={(e) => onToggleFavorite(e, dashboard.id)}
        >
          {dashboard.is_favorite ? (
            <Star className="fill-warning text-warning size-3.5" />
          ) : (
            <StarOff className="size-3.5" />
          )}
        </button>
        <button
          aria-label={t("dash.landing.delete")}
          type="button"
          className={
            variant === "collapsed"
              ? `text-muted-foreground hover:text-destructive rounded p-1 opacity-0 transition group-focus-within/${groupName}:opacity-100 group-hover/${groupName}:opacity-100`
              : "text-muted-foreground hover:text-destructive rounded p-1"
          }
          onClick={(e) => {
            e.stopPropagation()
            onDelete(dashboard)
          }}
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  )
}
